/**
 * Platform layer (docs/finished/tasting-notes-spec.md 6.1 and 11): device capabilities behind one
 * interface with a browser and a Capacitor implementation. Feature code never touches
 * `navigator.geolocation` or Capacitor plugins directly.
 */

export interface DevicePosition {
  lat: number;
  lng: number;
  /** Radius of the error circle in metres, when the platform reports it. */
  accuracyM: number | null;
  timestamp: number;
}

export type PositionErrorReason = 'denied' | 'unavailable' | 'timeout' | 'unsupported';

export class PositionError extends Error {
  constructor(
    public readonly reason: PositionErrorReason,
    message?: string,
  ) {
    super(message ?? reason);
    this.name = 'PositionError';
  }
}

export type LocationPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

export interface PhotoMetadata {
  position: { lat: number; lng: number } | null;
  takenAt: Date | null;
}

export interface PickedPhoto {
  file: File;
  /** Metadata read on the device before upload; the server strips EXIF from the file. */
  metadata: PhotoMetadata;
}

export interface OsmRequestInit {
  method?: 'GET' | 'POST';
  body?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface OsmResponse {
  status: number;
  json: () => Promise<unknown>;
}

/** Finds EAN/UPC codes in a camera frame. */
export interface BarcodeDetectorLike {
  readonly engine: 'native' | 'wasm';
  detect(source: HTMLVideoElement | HTMLCanvasElement | ImageBitmap): Promise<string[]>;
}

export interface Platform {
  readonly kind: 'web' | 'native';
  /** Reads the position once. Never watches. Rejects with PositionError. */
  getCurrentPosition(options?: {
    timeoutMs?: number;
    maximumAgeMs?: number;
  }): Promise<DevicePosition>;
  locationPermission(): Promise<LocationPermission>;
  /** EXIF position and capture time of a picked file (best effort in browsers). */
  readPhotoMetadata(file: Blob): Promise<PhotoMetadata>;
  /**
   * Opens the native camera or gallery. Returns null when the platform has no native
   * picker – the caller then falls back to an <input type="file">.
   */
  pickPhotos(source: 'camera' | 'gallery'): Promise<PickedPhoto[] | null>;
  isOnline(): boolean;
  onConnectivityChange(listener: (online: boolean) => void): () => void;
  /** Called when the app returns to the foreground (native) or the tab becomes visible. */
  onResume(listener: () => void): () => void;
  /**
   * HTTP for the OpenStreetMap services. Browsers are identified by their Referer; the
   * native shell sends an identifying User-Agent instead (OSM usage policies).
   */
  osmFetch(url: string, init?: OsmRequestInit): Promise<OsmResponse>;
  /**
   * Opens the platform's own barcode scanner (ML Kit in the native shell) and resolves with
   * the code, or null when the user cancelled. 'unsupported' means there is none – the caller
   * then renders its camera view and uses `createBarcodeDetector()`.
   */
  scanBarcodeNative(): Promise<string | null | 'unsupported'>;
  /** Decoder for the in-page camera view; works without a connection once it was loaded. */
  createBarcodeDetector(): Promise<BarcodeDetectorLike>;
}
